import { computed, onMounted, ref } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import customerService, { type Customer } from "@/services/customer";
import settingService, { type Setting } from "@/services/setting";
import { syncCustomerDisplayBranding } from "@/utils/posCustomerDisplay";
import warehouseService, { type Warehouse } from "@/services/warehouse";
import {
  applyGlobalSettings,
  buildAssetUrl,
  normalizeSetting,
} from "@/utils/settings";

const toFormValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "1" : "0";
  if (Array.isArray(value) || (typeof value === "object" && value !== null)) {
    return JSON.stringify(value);
  }
  return String(value);
};

export const useSystemSettings = () => {
  const { t } = useI18n();
  const settings = ref<Setting>(normalizeSetting());
  const customers = ref<Customer[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const isLoading = ref(false);
  const isSaving = ref(false);
  const logoFile = ref<File | null>(null);
  const logoPreview = ref<string>("");
  const posterImageFiles = ref<Record<number, File>>({});

  const logoUrl = computed(
    () => logoPreview.value || buildAssetUrl(settings.value.logo)
  );

  const syncCustomerDisplaySettings = (setting: Setting) => {
    syncCustomerDisplayBranding({
      site_name: setting.site_name,
      logo: setting.logo || "",
      company_phone: setting.company_phone || "",
      company_address: setting.company_address || "",
      company_email: setting.company_email || "",
      note_to_customer: setting.receipt_note_to_customer || "",
      currency_symbol: setting.default_currency === "KHR" ? "៛" : "$",
      posters: setting.customer_display_posters,
    });
  };

  const loadSettings = async () => {
    isLoading.value = true;
    try {
      const [settingResult, customerResult, warehouseResult] = await Promise.allSettled([
        settingService.getSettings(),
        customerService.getCustomers(),
        warehouseService.getWarehouses(),
      ]);

      if (settingResult.status === "rejected") {
        throw settingResult.reason;
      }

      settings.value = normalizeSetting(settingResult.value);
      customers.value =
        customerResult.status === "fulfilled" ? customerResult.value : [];
      warehouses.value =
        warehouseResult.status === "fulfilled" ? warehouseResult.value : [];
      applyGlobalSettings(settings.value);
      syncCustomerDisplaySettings(settings.value);

      if (customerResult.status === "rejected") {
        console.warn("Failed to load customers for system settings", customerResult.reason);
      }
      if (warehouseResult.status === "rejected") {
        console.warn("Failed to load warehouses for system settings", warehouseResult.reason);
      }
    } catch (error) {
      console.error("Failed to load system settings", error);
      await Swal.fire({
        icon: "error",
        title: t("system_settings_page.failed_to_load"),
        text: t("system_settings_page.unable_to_load_settings"),
        confirmButtonColor: "#605DFF",
      });
    } finally {
      isLoading.value = false;
    }
  };

  const handleLogoChange = (file?: File | null) => {
    logoFile.value = file || null;

    if (!file) {
      logoPreview.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      logoPreview.value = String(reader.result || "");
    };
    reader.readAsDataURL(file);
  };

  const handlePosterImageChange = (index: number, file?: File | null) => {
    if (!file) {
      delete posterImageFiles.value[index];
      return;
    }

    posterImageFiles.value[index] = file;
  };

  const saveSettings = async () => {
    isSaving.value = true;

    try {
      const formData = new FormData();
      Object.entries(settings.value).forEach(([key, value]) => {
        if (key === "logo") return;
        formData.append(key, toFormValue(value));
      });

      if (logoFile.value) {
        formData.append("logo", logoFile.value);
      }

      Object.entries(posterImageFiles.value).forEach(([index, file]) => {
        formData.append(`customer_display_poster_images[${index}]`, file);
      });

      const updated = normalizeSetting(await settingService.updateSettings(formData));
      settings.value = updated;
      applyGlobalSettings(updated);
      syncCustomerDisplaySettings(updated);
      logoFile.value = null;
      logoPreview.value = "";
      posterImageFiles.value = {};

      await Swal.fire({
        icon: "success",
        title: t("system_settings_page.settings_saved"),
        text: t("system_settings_page.settings_saved_successfully"),
        confirmButtonColor: "#605DFF",
      });
    } catch (error: any) {
      console.error("Failed to save system settings", error);
      await Swal.fire({
        icon: "error",
        title: t("system_settings_page.save_failed"),
        text:
          error?.response?.data?.message ||
          t("system_settings_page.unable_to_save_settings"),
        confirmButtonColor: "#605DFF",
      });
    } finally {
      isSaving.value = false;
    }
  };

  onMounted(loadSettings);

  return {
    settings,
    customers,
    warehouses,
    isLoading,
    isSaving,
    logoUrl,
    loadSettings,
    saveSettings,
    handleLogoChange,
    handlePosterImageChange,
  };
};
